import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { isScrollLocked } from "@/lib/scrollLock";
import { Toaster } from "sonner";
import "@/index.css";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import WhatsAppFab from "@/components/WhatsAppFab";
import ChatWidget from "@/components/ChatWidget";
import { EnquiryProvider } from "@/components/EnquiryModal";
import Home from "@/pages/Home";
import DomesticPackages from "@/pages/DomesticPackages";
import InternationalPackages from "@/pages/InternationalPackages";
import GroupTrips from "@/pages/GroupTrips";
import CustomisedTrips from "@/pages/CustomisedTrips";
import AboutUs from "@/pages/AboutUs";
import ContactUs from "@/pages/ContactUs";
import Policies from "@/pages/Policies";

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-ink p-8 text-center">
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-aurora">Cosmos Routes</p>
            <p className="mt-4 font-display text-2xl font-bold text-slate-100">Something drifted off course.</p>
            <button
              data-testid="error-reload-btn"
              onClick={() => window.location.reload()}
              className="mt-6 rounded-full bg-aurora px-6 py-3 text-sm font-semibold text-ink"
            >
              Re-enter orbit
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function ScrollManager() {
  const { pathname } = useLocation();
  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.08, smoothWheel: true });
    window.__lenis = lenis;
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    // Safety net: if nothing holds a scroll lock, Lenis must never stay stopped.
    const ensureRunning = () => {
      if (isScrollLocked()) return;
      if (lenis.isStopped) lenis.start();
      if (document.body.style.overflow === "hidden") document.body.style.overflow = "";
    };
    const opts = { passive: true, capture: true };
    window.addEventListener("wheel", ensureRunning, opts);
    window.addEventListener("touchstart", ensureRunning, opts);
    window.addEventListener("keydown", ensureRunning, opts);
    window.addEventListener("focus", ensureRunning);
    return () => {
      window.removeEventListener("wheel", ensureRunning, opts);
      window.removeEventListener("touchstart", ensureRunning, opts);
      window.removeEventListener("keydown", ensureRunning, opts);
      window.removeEventListener("focus", ensureRunning);
      cancelAnimationFrame(raf);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);
  useEffect(() => {
    if (window.__lenis && !isScrollLocked()) {
      window.__lenis.start();
      document.body.style.overflow = "";
    }
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true, force: true });
    else window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function App() {
  return (
    <ErrorBoundary>
      <EnquiryProvider>
        <BrowserRouter>
          <ScrollManager />
          <div className="relative min-h-screen bg-ink font-body text-slate-100">
            <Navbar />
            <main>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/domestic-packages" element={<DomesticPackages />} />
                <Route path="/international-packages" element={<InternationalPackages />} />
                <Route path="/group-trips" element={<GroupTrips />} />
                <Route path="/customised-trips" element={<CustomisedTrips />} />
                <Route path="/about-us" element={<AboutUs />} />
                <Route path="/contact-us" element={<ContactUs />} />
                <Route path="/policies" element={<Policies />} />
                <Route path="*" element={<Home />} />
              </Routes>
            </main>
            <Footer />
            <WhatsAppFab />
            <ChatWidget />
          </div>
        </BrowserRouter>
        <div className="grain-overlay" />
        <Toaster theme="dark" position="top-right" richColors />
      </EnquiryProvider>
    </ErrorBoundary>
  );
}

export default App;
